import { useCallback } from 'react';
import { getFileType, getOriginalUrl, getProcessedPhotoUrl } from '../utils/photoHelpers';

/**
 * Custom hook for handling photo download functionality
 * @param {Set} selectedPhotos - Set of selected photo filenames
 * @param {function} onDownloadComplete - Callback when download is complete
 * @returns {Object} Download functions and state
 */
export const usePhotoDownload = (selectedPhotos, onDownloadComplete) => {
  /**
   * Download files individually (fallback for desktop or when sharing fails)
   * @param {Array} files - Array of file objects with filename and blob
   */
  const downloadFilesIndividually = useCallback((files) => {
    // Show progress indicator for multiple files
    if (files.length > 1) {
      const progressDiv = document.createElement('div');
      progressDiv.innerHTML = `
        <div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
                    background: rgba(0,0,0,0.9); color: white; padding: 20px; border-radius: 8px;
                    text-align: center; z-index: 9999;">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-wedding-sage mx-auto mb-2"></div>
          <p>Downloading ${files.length} files...</p>
          <p style="font-size: 12px; opacity: 0.8;">Files will be saved to your Downloads folder</p>
        </div>
      `;
      document.body.appendChild(progressDiv);

      // Download files with a small delay to prevent browser blocking
      files.forEach(({ filename, blob }, index) => {
        setTimeout(() => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          // Remove progress indicator after last file
          if (index === files.length - 1) {
            setTimeout(() => {
              document.body.removeChild(progressDiv);
            }, 1000);
          }
        }, index * 200); // 200ms delay between downloads
      });
    } else {
      // Single file download
      const { filename, blob } = files[0];
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, []);

  /**
   * Download selected photos
   */
  const downloadSelectedPhotos = useCallback(async () => {
    if (selectedPhotos.size === 0) return;

    // Show loading indicator
    const loadingDiv = document.createElement('div');
    loadingDiv.innerHTML = `
      <div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
                  background: rgba(0,0,0,0.9); color: white; padding: 20px; border-radius: 8px;
                  text-align: center; z-index: 9999;">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-wedding-sage mx-auto mb-2"></div>
        <p>Preparing ${selectedPhotos.size} files for download...</p>
      </div>
    `;
    document.body.appendChild(loadingDiv);

    // Prepare files for download
    const downloadPromises = Array.from(selectedPhotos).map(async (filename) => {
      try {
        const fileType = getFileType(filename);

        if (fileType === 'video') {
          // For videos, download the original file
          const videoUrl = getOriginalUrl(filename);
          const response = await fetch(videoUrl);
          const blob = await response.blob();
          return { filename, blob, type: 'video' };
        } else {
          // For images, download the processed webp version
          const photoUrl = getProcessedPhotoUrl(filename);
          const response = await fetch(photoUrl);
          const blob = await response.blob();
          return { filename, blob, type: 'image' };
        }
      } catch (error) {
        console.error(`Failed to download ${filename}:`, error);
        return null;
      }
    });

    Promise.all(downloadPromises)
      .then((results) => {
        const validResults = results.filter(result => result !== null);

        if (validResults.length === 0) {
          alert('Failed to download any files. Please try again.');
          return;
        }

        // For mobile devices, use Web Share API if available and supported for files
        if (navigator.share && navigator.canShare) {
          // Prepare files for sharing
          const sharePromises = validResults.map(async ({ filename, blob }) => {
            return new File([blob], filename, {
              type: blob.type || 'application/octet-stream'
            });
          });

          Promise.all(sharePromises).then((files) => {
            // Check if the browser can actually share these files
            if (navigator.canShare({ files: files })) {
              navigator.share({
                title: validResults.length === 1 ? 'Wedding Photo' : 'Wedding Photos',
                text: validResults.length === 1
                  ? 'Wedding photo'
                  : `Sharing ${files.length} wedding photos`,
                files: files
              }).then(() => {
                console.log('Shared successfully');
              }).catch((error) => {
                console.log('Share error:', error);

                // Only fallback to download for actual errors, not user actions
                // AbortError: user cancelled the dialog
                // NotAllowedError: user denied permission
                if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
                  console.log('User cancelled sharing or denied permission - no download triggered');
                } else {
                  console.log('Share failed due to technical error, falling back to download:', error);
                  // Fallback to download only for actual technical failures
                  downloadFilesIndividually(validResults);
                }
              });
            } else {
              // Browser can't share files, use download
              downloadFilesIndividually(validResults);
            }
          });
        } else {
          // For desktop or when sharing is not available, use download
          downloadFilesIndividually(validResults);
        }

        // Call completion callback if provided
        if (onDownloadComplete) {
          onDownloadComplete();
        }
      })
      .catch((error) => {
        console.error('Download error:', error);
        alert('Failed to download files. Please try again.');
      })
      .finally(() => {
        // Remove loading indicator
        document.body.removeChild(loadingDiv);
      });
  }, [selectedPhotos, downloadFilesIndividually, onDownloadComplete]);

  return {
    downloadSelectedPhotos,
    downloadFilesIndividually
  };
};
